/* eslint-disable */
import React, { Suspense, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useArcSettings } from '../hooks/useArcSettings';
import { useAccountInfoSettings } from '../../account-info/hooks/useAccountInfoSettings';
import { TcsmStates } from '../../../root/tcsm-model';
import { ArcRoutes } from '../constants/routes';
import { withForbidden } from '../../../global/components/HOCs/with-forbidden';
import { TcsmFeatures } from '../../../global/models/features-model';
import { useInstitutionList } from '../hooks/useInstitutionList';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { setUserContext } from '../../../global/ducks/app-context';
import { UserContext } from '../../../global/models/app-context-model';
import { useAdminStatus } from '../../../global/hooks/useAdminStatus';
import { ForbiddenErrorMessage } from '../../../global/components/forbidden-error-message';
import { PersonManagementApp } from '../../account-info/components/person-management-app';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { withLayout } from '../../../global/components/HOCs/with-layout';
import { GpsType, LayoutModels } from '../../../global/components/layout/layout-models';
import { GroupsContainer } from '../features/groups/groups-container';
import { OfficerAdminAppKeys, OfficerAdminContext } from '../../../global/models/officer-admin-model';
import { AnnualEnrollmentRoutes } from '../../annual-enrollment/constants/annual-enrollment-routes';
import { NewHireConfirmation } from '../features/new-hire/components/new-hire-confirmation';
import { MapLanding } from '../features/map-landing/map-landing';
import { NotificationAlertPage } from '../../notification/notification-page';
import { EmploymentTerminationConfirmation } from '../features/termination/components/employment-termination-confirmation';
import { PageFactory } from '../../../global/components/page-factory';
import { withParticipantSelect } from '../../../global/components/HOCs/with-participant-select';
import { withInstitutionSelect } from '../../../global/components/HOCs/with-institution-select';
import { TcsmSiteSettingsProps } from '../../../global/models/epi-model';
import { useMapMaintenance } from '../../../global/hooks/useMaintenance';
import { MaintenancePage } from '../../../global/components/maintenance-page';
import { TcsmAppConfigs } from '../../../global/models/configuration-models';
import { MapInfoSection } from './map-info-section';
import { AEDeeplinkRedirect } from './ae-deeplink-redirect';
import DioceseInstitutionSearch from '../features/diocese-institution-search/diocese-institution-search';
import { useRoleObject } from './useRoles';
import { NewHireReview } from '../features/new-hire/components/new-hire-review';
import { NewHireEnrollmentRoutes } from '../features/new-hire-enrollment/models/new-hire-routes';
import { useNewHireWhoWillEnrollContentModel } from '../features/new-hire-enrollment/hooks/useNewHireWhoWillEnrollContentModel';
import { useUpdateEnrollmentWhoWillEnrollContentModel } from '../features/new-hire-enrollment/hooks/useUpdateEnrollmentWhoWillEnrollContentModel';
import { ConfirmationStatusCode } from '../features/new-hire-enrollment/models/new-hire-confirmation-models';
import { InstitutionContext, InstitutionContextKeys } from '../../../global/models/institution-model';
import { withManageInstitution } from '../../../Global/components/HOCs/with-manage-institution-select';
import { HoursIncreaseEventType, NewHireEventType } from '../constants/constant-strings';
import { isInstitutionOfficerOnly } from '../../../global/helpers/auth-helpers';
import { InstitutionProps } from '../models/arc-epi-props';

const ReportsInstitution = React.lazy(() => import('../features/institutions/reports/reports-institution'));
const ReportsDiocesan = React.lazy(() => import('../features/institutions/reports/reports-diocesan'));
const DioceseSearch = React.lazy(() => import('../features/diocese-search/diocese-search'));
const EmployeeChangeLog = React.lazy(() => import('../../../backend-search/change-log/change-log-search'));
const InstitutionSearch = React.lazy(() => import('../features/institution-search/institution-search'));
const InstitutionLanding = React.lazy(() => import('../features/institutions/institution-landing/institution-landing'));
const InstitutionDetails = React.lazy(() => import('../features/institutions/institution-details'));
const InstitutionAddRequestApp = React.lazy(() => import('../features/institutions/institution-add-request'));
const InstitutionAddOrRemoveConfirmation = React.lazy(() => import('../features/institutions/institution-add-request-submitted'));
const InstitutionRemoveRequestApp = React.lazy(() => import('../features/institutions/institution-remove-request'));
const InstituionSurvey = React.lazy(() => import('../features/institutions/institution-survey/institution-survey'));
const InstitutionCompensationApp = React.lazy(() => import('../features/institutions/compensation/institution-compensation'));
const PlanSummary = React.lazy(() => import('../features/institutions/plan-summary/plan-summary'));
const EmployeeSearch = React.lazy(() => import('../features/employee-search/employee-search'));
const OfficerSearch = React.lazy(() => import('../features/institution-officers/institution-officers-search'));
const AddOfficer = React.lazy(() => import('../features/institution-officers/institution-officer-add'));
const AnnualEnrollmentInProgress = React.lazy(() => import('../../annual-enrollment/components/annual-enrollment-progress'));
const AccountInfoContainer = React.lazy(() => import('../../account-info/components/account-info-container'));
const AnnualEnrollmentApp = React.lazy(() => import('../../annual-enrollment/components/annual-enrollment-app'));
const ParticipantEnrollmentApp = React.lazy(() => import('../features/new-participant-enrollment/participant-enrollment-app'));
const NewHireEnrollmentApp = React.lazy(() => import('../features/new-hire-enrollment/new-hire-enrollment-app'));
const DioApproverEnrollmentApp = React.lazy(() => import('../features/new-hire-enrollment/admin-approver/dio-approver-enrollment-app'));
const PlanHistory = React.lazy(() => import('../features/institutions/plan-history/plan-history'));
const DbPlanAdoption = React.lazy(() => import('../features/institutions/plan-adoption/db-adoption/db-plan-adoption'));
const QccoPlanAdoption = React.lazy(() => import('../features/institutions/plan-adoption/qcco-adoption/qcco-plan-adoption'));
const QccoPlanAmendment = React.lazy(() => import('../features/institutions/plan-adoption/qcco-amendment/qcco-plan-amendment'));
const DeclineMedicalDental = React.lazy(() => import('../features/institutions/decline-plan/decline-medical-dental'));
const DeathOfChild = React.lazy(() => import('../../account-info/features/death-of-child/death-of-child'));

const ReportsInstitutionWrapper = withInstitutionSelect()(withLayout(LayoutModels.NO_LAY_OUT)(ReportsInstitution));
const ReportsDiocesanWrapper = withLayout(LayoutModels.NO_LAY_OUT)(ReportsDiocesan);
const DioceseSearchWrapper = withLayout(null, GpsType.DIOCESE_LIST)(withForbidden(TcsmFeatures.DioceseSearch)(DioceseSearch));
const InstitutionComponent = withLayout(null, GpsType.INSTITUTION_LIST)(withForbidden(TcsmFeatures.InstitutionSearch)(InstitutionSearch));
const DioceseInstitutionSearchComponent = withLayout(null, GpsType.INSTITUTION_LIST)(withForbidden(TcsmFeatures.InstitutionSearch)(DioceseInstitutionSearch));
const InstitutionLandingComponent = withForbidden(TcsmFeatures.Institution)(InstitutionLanding);
const InstitutionDetailsComponent = withInstitutionSelect()(withLayout(null, GpsType.INSTITUTION_DETAIL)(withForbidden(TcsmFeatures.Institution)(InstitutionDetails)));
const InstitutionAddRequestComponent = (withLayout(null, GpsType.INSTITUTION_ADD_REQUEST)(withForbidden(TcsmFeatures.Institution)(InstitutionAddRequestApp)));
const InstitutionAddOrRemoveConfirmationComponent = (withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.Institution)(InstitutionAddOrRemoveConfirmation)));
const InstitutionRemoveRequestComponent = withManageInstitution()(withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.Institution)(InstitutionRemoveRequestApp)));
const InstitutionRemoveRequestSubmittedComponent = (withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.Institution)(InstitutionAddOrRemoveConfirmation)));
const InstitutionSurveyComponent = withInstitutionSelect()(withLayout(null, GpsType.INSTITUTION_SURVEY)(withForbidden(TcsmFeatures.Institution)(InstituionSurvey)));
const InstitutionCompensation = withInstitutionSelect()(withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.Institution)(InstitutionCompensationApp)));
const PlanSummaryComponent = withInstitutionSelect()(withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.Institution)(PlanSummary)));
const EmployeeSearchComponent = withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.EmployeeSearch)(EmployeeSearch as any));
const OfficerSearchComponent = withInstitutionSelect()(withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.InstitutionAdministratorSearch)(OfficerSearch)));
const AddOfficerComponent = withInstitutionSelect() (withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.AddInstitutionAdministrator)(AddOfficer)));
const AEInProgress = withLayout(null, GpsType.AE_INTERRUPT)(withForbidden(TcsmFeatures.AEConfirmation)(AnnualEnrollmentInProgress));
const NewHireConfirmationWrapper = withLayout(LayoutModels.NO_LAY_OUT)(AccountInfoContainer);
const TerminationConfirmationWrapper = withLayout(LayoutModels.NO_LAY_OUT)(AccountInfoContainer);
const AnnualEnrollmentWrapper = withParticipantSelect()(AnnualEnrollmentApp);
const EmployeeChangeLogWrapper = withInstitutionSelect()(EmployeeChangeLog);
const PlanHistoryComponent = withInstitutionSelect()(withLayout(LayoutModels.NO_LAY_OUT)(PlanHistory));
const DbPlanAdoptionWrapper = withInstitutionSelect()(DbPlanAdoption);
const QccoPlanAdoptionWrapper = withInstitutionSelect()(QccoPlanAdoption);
const QccoPlanAmendmentWrapper = withInstitutionSelect()(QccoPlanAmendment);
const DeclineMedicalDentalWrapper = withInstitutionSelect()(DeclineMedicalDental);

interface Props {
    appConfig: TcsmAppConfigs
}
export function ArcApp(props: Props) {
	const { appConfig } = props;
	const app = useContext(DemographicsContext);
	const roles = useRoleObject();
	const isAdmin = useAdminStatus(roles);
	const institutionOfficerOnly = isInstitutionOfficerOnly();
	const routeMatch = useRouteMatch();
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const arcSettingsLoaded = useArcSettings(appConfig.compensationSettingsId,
		appConfig.employmentSettingsId,
		appConfig.institutionSettingsId,
		appConfig.searchSettingsId,
		appConfig.personSearchSettingsId,
		appConfig.midYearSettingsId,
		appConfig.mapPortalSettingsId);

    const demographicsSettingsLoaded = useAccountInfoSettings(appConfig.personalInfoSettingId, appConfig.relationshipsSettingId);
	const instutitonSettings = useSelector<TcsmStates, InstitutionProps>(state => state.epiProps?.institutionSettingsState?.epiContent);
    const institutionListLoaded = useInstitutionList();
    const dispatch = useDispatch();
    const mapMaintenance = useMapMaintenance();

    useEffect(() => {
        if (app === DemographicAppKeys.arc) {
            dispatch(setUserContext(UserContext.ARC));
        }

        if (isAdmin) {
            dispatch(setUserContext(UserContext.ARC));
        }
    }, [isAdmin, app]);

    if (!isAdmin) {
        return <ForbiddenErrorMessage />;
    }

    if (!arcSettingsLoaded || !demographicsSettingsLoaded || !institutionListLoaded) {
        return <LoadingCircle />;
    }

    if (mapMaintenance) {
        return <MaintenancePage message={siteSettings?.mapSiteMaintenanceText} />;
    }

	return (
		<>
			<DemographicsContext.Provider value={DemographicAppKeys.arc}>
				<MapInfoSection />
				<Suspense fallback={<LoadingCircle />}>
					<Switch>
						<Route exact path={`${routeMatch.path}`} render={() => <MapLanding />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}`} render={() => <InstitutionLandingComponent />} />
						<Route exact path={`${routeMatch.path}${AnnualEnrollmentRoutes.InProgress}`} render={() => <AEInProgress />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`} render={() => <InstitutionComponent />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.ManageInstitutionSearch}`} render={() =>
							<InstitutionContext.Provider value={InstitutionContextKeys.manageInstitution}>
								<InstitutionComponent />
							</InstitutionContext.Provider>
						} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.DioceseRoot}${ArcRoutes.InstitutionSearch}`} render={() => <DioceseInstitutionSearchComponent />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.DioceseRoot}`} render={() => <DioceseSearchWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionDetails}`} render={() => <InstitutionDetailsComponent />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionAddRequest}`} render={() => <InstitutionAddRequestComponent />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionAddRequestSubmitted}`} render={() => <InstitutionAddOrRemoveConfirmationComponent model={{
							title: instutitonSettings.submittedPageTitle,
							description: instutitonSettings.submittedDescription,
							nextStep: instutitonSettings.institutionAddRequestSubmittedNextSteps,
							successMessage: instutitonSettings.submittedSuccessMessage,
							gps: GpsType.INSTITUTION_ADD_REQUEST_SUBMITTED,
							mapHomeButtonText: instutitonSettings.mapHomeButtonText,
						}} />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionRemoveRequest}`} render={() => <InstitutionRemoveRequestComponent />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionRemoveRequestSubmitted}`} render={() => <InstitutionRemoveRequestSubmittedComponent model={{
							title: instutitonSettings.institutionRemoveRequestSubmittedPageTitle,
							description: instutitonSettings.institutionRemoveRequestSubmittedDescription,
							nextStep: instutitonSettings.institutionRemoveRequestSubmittedNextSteps,
							successMessage: instutitonSettings.institutionRemoveRequestSubmittedSuccessMessage,
							gps: GpsType.INSTITUTION_REMOVE_REQUEST_SUBMITTED,
							mapHomeButtonText: instutitonSettings.mapHomeButtonText,
						}} />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.PlanSummary}`} render={() => <PlanSummaryComponent />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.PlanHistory}`} render={() => <PlanHistoryComponent />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.DbPlanAdoption}`} render={() => <DbPlanAdoptionWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.QccoPlanAdoption}`} render={() => <QccoPlanAdoptionWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.QccoPlanAmend}`} render={() => <QccoPlanAmendmentWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.QccoPlanIndemnify}`} render={() => <QccoPlanAdoptionWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.GhlDecline}`} render={() => <DeclineMedicalDentalWrapper />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSurvey}`} render={() => <InstitutionSurveyComponent />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.Compensation}`} render={() => <InstitutionCompensation />} />
						<Route path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.EmployeeChangeLog}`} render={() => <EmployeeChangeLogWrapper />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.ReportsInstitution}`}>
							<ReportsInstitutionWrapper />
						</Route>
						<Route exact path={`${routeMatch.path}${ArcRoutes.ReportsDiocesan}`}>
							<ReportsDiocesanWrapper />
						</Route>
						<Route path={`${routeMatch.path}${ArcRoutes.GroupsLanding}`} render={() => <GroupsContainer
							appConfig={appConfig}
						/>} />
						<Route
							exact
							path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionAdministratorSearch}`}
							render={() => (
								<OfficerAdminContext.Provider value={OfficerAdminAppKeys.officer}>
									<OfficerSearchComponent />
								</OfficerAdminContext.Provider>
							)} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionAdministratorSearch}${ArcRoutes.AddInstitutionAdministrator}`} render={() => <AddOfficerComponent />} />
						<Route exact path={`${routeMatch.path}${ArcRoutes.PeopleRoot}`} render={() => <PageFactory />} />
						<Route exact path={[`${routeMatch.path}${ArcRoutes.PeopleRoot}${ArcRoutes.EmployeeList}`,
						`${routeMatch.path}${ArcRoutes.PeopleRoot}${ArcRoutes.EmployeeSearch}`]}
							render={() => (
								<EmployeeSearchComponent
									defaultOnEmployeeSelectRedirectUrl={`${routeMatch.path}${ArcRoutes.PeopleRoot}${AccountInfoRoutes.PersonalInfoSegment}`}
								/>
							)}
						/>

						<Route
							path={`${routeMatch.path}${ArcRoutes.PeopleRoot}`}
							render={() => (
								<PersonManagementApp
									appConfig={appConfig}
									isArc
								/>
							)}
						/>
						<Route exact
							path={`${routeMatch.path}${AnnualEnrollmentRoutes.Resources}${AnnualEnrollmentRoutes.AdminAnnualEnrollmentResources}${AnnualEnrollmentRoutes.ClientId}/:clientid${AnnualEnrollmentRoutes.GroupId}/:groupId${AnnualEnrollmentRoutes.BillingAccountId}/:billingAccountId`}
							render={() => (
								<AEDeeplinkRedirect />
							)}
						/>
						<Route
							path={`${routeMatch.path}${AnnualEnrollmentRoutes.AnnualEnrollment}`}
							render={() => (
								<AnnualEnrollmentWrapper
									annualEnrollmentSettingId={appConfig.annualEnrollmentSettingsId}
									personalInfoSettingId={appConfig.personalInfoSettingId}
									relationshipsSettingId={appConfig.relationshipsSettingId}
								/>
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.NonStipNewHire}`}
							render={() => (
								<Redirect to={`${routeMatch.path}${ArcRoutes.NewHireEnrollment}${NewHireEnrollmentRoutes.EmployeeRecord}${ArcRoutes.NonStipNewHire}`} />
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.StipNewHire}`}
							render={() => (
								<Redirect to={`${routeMatch.path}${ArcRoutes.NewHireEnrollment}${NewHireEnrollmentRoutes.EmployeeRecord}${ArcRoutes.StipNewHire}`} />
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.NewHireConfirmation}`}
							render={() => (
								<NewHireConfirmationWrapper>
									<NewHireConfirmation settingsId={appConfig.newHireSettingsId} />
								</NewHireConfirmationWrapper>
							)}
						/>

						<Route exact path={`${routeMatch.path}${ArcRoutes.NewHireReview}`}>
							<NewHireReview />
						</Route>

                        <Route exact path={`${routeMatch.path}${ArcRoutes.EmploymentTerminationConfirmation}`}
                            render={() => (
                                <TerminationConfirmationWrapper>
                                    <EmploymentTerminationConfirmation />
                                </TerminationConfirmationWrapper>
                            )}
                        />
                        <Route exact path={`${routeMatch.path}${ArcRoutes.UnpaidLeaveOfAbsence}`}
                            render={() => (
                                <TerminationConfirmationWrapper>
                                    <EmploymentTerminationConfirmation />
                                </TerminationConfirmationWrapper>
                            )}
                        />


						<Route exact path={`${routeMatch.path}${ArcRoutes.Notifications}`} render={() => <NotificationAlertPage />} />
						<Route path={`${routeMatch.path}${ArcRoutes.UpdateEnrollment}`}>
							<NewHireEnrollmentApp appSettings={appConfig} eventType={HoursIncreaseEventType} contentModelHook={useUpdateEnrollmentWhoWillEnrollContentModel} confirmationCode={institutionOfficerOnly ? ConfirmationStatusCode.UPDATE_ENROLLMENT : ConfirmationStatusCode.UPDATE_ENROLLMENT_NO_APPROVAL} />
						</Route>
						<Route path={`${routeMatch.path}${ArcRoutes.NewHireEnrollment}`}>
							<NewHireEnrollmentApp appSettings={appConfig} eventType={NewHireEventType} contentModelHook={useNewHireWhoWillEnrollContentModel} />
						</Route>
						<Route path={`${routeMatch.path}${ArcRoutes.DioApproverEnrollment}`}>
							<DioApproverEnrollmentApp appSettings={appConfig} />
						</Route>
						<Route path={`${routeMatch.path}${ArcRoutes.ParticipantEnrollment}`}>
							<ParticipantEnrollmentApp appSettings={appConfig} />
						</Route>
						<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DeathOfChild}`} render={() => (
						<>
							<DeathOfChild />
						</>
						)} />
						<Route path={`${routeMatch.path}/*`}>
							<PageFactory hideBreadcrumbs />
						</Route>
					</Switch>
				</Suspense>
			</DemographicsContext.Provider>

        </>
    );
}
